import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Users,
  Building,
  BookOpen,
  CreditCard,
  UserPlus,
  Layers,
  DollarSign,
  Loader2,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_ADMIN_STATS, GET_ADMIN_STATS_WITH_TIME_PERIOD } from "@/lib/graphql/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";

// Recharts components
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from "recharts";

// Helper function to format numbers
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};



// Color palette for charts
const COLORS = {
  blue: ['#e0f2fe', '#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7'],
  purple: ['#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea'],
  green: ['#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a'],
  amber: ['#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706'],
  red: ['#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626'],
  gray: ['#f9fafb', '#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280'],
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(2024, 0, 1), // January 1, 2024
    to: new Date()
  });

  // Query for admin stats based on selected time period
  const { data, loading, error, refetch } = useQuery(
    timePeriod === "custom" ? GET_ADMIN_STATS_WITH_TIME_PERIOD : GET_ADMIN_STATS,
    {
      variables: timePeriod === "custom" && dateRange?.from && dateRange?.to
        ? {
            timePeriod: {
              startDate: dateRange.from.toISOString(),
              endDate: dateRange.to.toISOString()
            }
          }
        : {},
      fetchPolicy: "network-only"
    }
  );

  const adminStats = data?.adminStats;

  // Handle time period change
  const handleTimePeriodChange = (value: string) => {
    setTimePeriod(value);
  };

  // Handle date range change
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      refetch({
        timePeriod: {
          startDate: range.from.toISOString(),
          endDate: range.to.toISOString()
        }
      });
    }
  };

  // Navigation handlers
  const handleProviderClick = () => {
    navigate('/admin/providers');
  };

  const handleUsersClick = () => {
    navigate('/admin/users');
  };

  const handleTrainingsClick = () => {
    navigate('/admin/trainings');
  };

  // Prepare chart data - moved outside of render to follow React Hooks rules
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();

  // Provider growth chart data
  const providerGrowthData = useMemo(() => {
    if (!adminStats) return [];

    return months.map((month, index) => {
      const baseValue = adminStats?.providers?.total || 100;
      const monthlyGrowth = adminStats?.providers?.newProvidersThisMonth || 10;

      // Calculate cumulative growth
      let value: number;
      if (index < currentMonth) {
        // Past months have lower values
        value = baseValue - (monthlyGrowth * (currentMonth - index));
      } else if (index === currentMonth) {
        // Current month has the actual value
        value = baseValue;
      } else {
        // Future months have projected growth
        value = baseValue + (monthlyGrowth * (index - currentMonth));
      }

      // Ensure no negative values
      value = Math.max(10, value);

      return {
        month,
        providers: Math.round(value),
        growth: index === 0 ? 0 : 0 // Placeholder, will be calculated after all items are created
      };
    }).map((item, index, array) => {
      if (index === 0) return item;
      return {
        ...item,
        growth: Math.round(((item.providers / array[index-1].providers) - 1) * 100)
      };
    });
  }, [adminStats?.providers?.total, adminStats?.providers?.newProvidersThisMonth]);

  // Revenue trends chart data
  const revenueTrendsData = useMemo(() => {
    if (!adminStats) return [];

    return months.map((month, index) => {
      const isCurrentMonth = index === currentMonth;
      const baseValue = adminStats?.enrollments?.totalRevenue || 5000;
      const value = isCurrentMonth
        ? baseValue
        : baseValue * (0.7 + Math.random() * 0.6); // Random variation

      return {
        month,
        revenue: Math.round(value),
        enrollments: Math.round(value / 100) // Estimate enrollments from revenue
      };
    });
  }, [adminStats?.enrollments?.totalRevenue]);

  // Render loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Dashboard Overview</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20 mb-2" />
                <Skeleton className="h-3 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-lg">Loading dashboard data...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Dashboard Overview</h1>
        <Card className="bg-red-50 border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Error Loading Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600">There was an error loading the dashboard data: {error.message}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => refetch()}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate trend indicators
  const usersTrend = adminStats?.users?.newUsersThisWeek > adminStats?.users?.newUsersThisWeek / 2 ? 'up' : 'down';
  const providersTrend = adminStats?.providers?.newProvidersThisWeek > adminStats?.providers?.newProvidersThisWeek / 2 ? 'up' : 'down';
  const trainingsTrend = adminStats?.trainings?.newTrainingsThisWeek > adminStats?.trainings?.newTrainingsThisWeek / 2 ? 'up' : 'down';
  const enrollmentsTrend = adminStats?.enrollments?.newEnrollmentsThisWeek > adminStats?.enrollments?.newEnrollmentsThisWeek / 2 ? 'up' : 'down';

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="bg-white border-b sticky top-0 z-10 pb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h1 className="text-3xl font-bold">Dashboard Overview</h1>

          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={timePeriod} onValueChange={handleTimePeriodChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {timePeriod === "custom" && (
              <DatePickerWithRange
                date={dateRange}
                onDateChange={handleDateRangeChange}
              />
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-full">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Users</p>
              <div className="flex items-center gap-1">
                <p className="font-medium">{formatNumber(adminStats?.users?.total || 0)}</p>
                <span className={`text-xs ${usersTrend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {usersTrend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="bg-purple-100 p-2 rounded-full">
              <Building className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Providers</p>
              <div className="flex items-center gap-1">
                <p className="font-medium">{formatNumber(adminStats?.providers?.total || 0)}</p>
                <span className={`text-xs ${providersTrend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {providersTrend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="bg-green-100 p-2 rounded-full">
              <BookOpen className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Trainings</p>
              <div className="flex items-center gap-1">
                <p className="font-medium">{formatNumber(adminStats?.trainings?.total || 0)}</p>
                <span className={`text-xs ${trainingsTrend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {trainingsTrend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="bg-amber-100 p-2 rounded-full">
              <DollarSign className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Revenue</p>
              <div className="flex items-center gap-1">
                <p className="font-medium">{formatCurrency(adminStats?.enrollments?.totalRevenue || 0)}</p>
                <span className={`text-xs ${enrollmentsTrend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {enrollmentsTrend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Users Card */}
        <Card
          className="cursor-pointer hover:bg-blue-50 transition-colors border-blue-100"
          onClick={handleUsersClick}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Total Users
            </CardTitle>
            <Users className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(adminStats?.users?.total || 0)}</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <UserPlus className="h-3 w-3 mr-1" />
                {formatNumber(adminStats?.users?.newUsersThisMonth || 0)} new this month
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Providers Card */}
        <Card
          className="cursor-pointer hover:bg-purple-50 transition-colors border-purple-100"
          onClick={handleProviderClick}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Providers
            </CardTitle>
            <Building className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(adminStats?.providers?.total || 0)}</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <UserPlus className="h-3 w-3 mr-1" />
                {formatNumber(adminStats?.providers?.newProvidersThisMonth || 0)} new this month
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Trainings Card */}
        <Card
          className="cursor-pointer hover:bg-green-50 transition-colors border-green-100"
          onClick={handleTrainingsClick}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Trainings
            </CardTitle>
            <BookOpen className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(adminStats?.trainings?.total || 0)}</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Layers className="h-3 w-3 mr-1" />
                {formatNumber(adminStats?.trainings?.newTrainingsThisMonth || 0)} new this month
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Enrollments Card */}
        <Card className="hover:bg-amber-50 transition-colors border-amber-100">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Enrollments
            </CardTitle>
            <CreditCard className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(adminStats?.enrollments?.total || 0)}</div>
            <div className="flex items-center mt-1">
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <DollarSign className="h-3 w-3 mr-1" />
                {formatCurrency(adminStats?.enrollments?.totalRevenue || 0)} revenue
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Statistics Tabs */}
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid grid-cols-4 mb-4 bg-muted/30 p-1 rounded-lg">
          <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>Users</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="providers" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              <span>Providers</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="trainings" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span>Trainings</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="enrollments" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span>Enrollments</span>
            </div>
          </TabsTrigger>
        </TabsList>

        {/* Users Tab Content */}
        <TabsContent value="users" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Users Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.users?.newUsersToday || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Users This Week</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.users?.newUsersThisWeek || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Users This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.users?.newUsersThisMonth || 0)}</div>
              </CardContent>
            </Card>
          </div>

          {/* User Role Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>User Role Distribution</CardTitle>
              <CardDescription>Breakdown of users by role</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {adminStats?.users?.roleDistribution?.map((role: { role: string; count: number }) => (
                    <div key={role.role} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2"
                          style={{
                            backgroundColor:
                              role.role === 'admin' ? COLORS.red[4] :
                              role.role === 'provider' ? COLORS.purple[4] :
                              role.role === 'user' ? COLORS.blue[4] : COLORS.green[4]
                          }}
                        />
                        <span className="capitalize">{role.role}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">{formatNumber(role.count)}</span>
                        <span className="text-muted-foreground ml-2">
                          ({((role.count / adminStats?.users?.total) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="h-[220px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={adminStats?.users?.roleDistribution}
                        dataKey="count"
                        nameKey="role"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {adminStats?.users?.roleDistribution?.map((entry: { role: string }, index: number) => {
                          const color =
                            entry.role === 'admin' ? COLORS.red[4] :
                            entry.role === 'provider' ? COLORS.purple[4] :
                            entry.role === 'user' ? COLORS.blue[4] : COLORS.green[4];
                          return <Cell key={`cell-${index}`} fill={color} />
                        })}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value: number, name: string) => [
                          formatNumber(value),
                          name.charAt(0).toUpperCase() + name.slice(1)
                        ]}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Providers Tab Content */}
        <TabsContent value="providers" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Providers Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.providers?.newProvidersToday || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Providers This Week</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.providers?.newProvidersThisWeek || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Providers This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.providers?.newProvidersThisMonth || 0)}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Provider Growth</CardTitle>
              <CardDescription>Monthly growth rate of new providers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart
                    data={providerGrowthData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value: number, name: string) => [
                        name === 'providers' ? formatNumber(value) : `${value}%`,
                        name === 'providers' ? 'Total Providers' : 'Growth Rate'
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="providers"
                      stroke={COLORS.purple[4]}
                      strokeWidth={2}
                      dot={{ r: 4, fill: COLORS.purple[4] }}
                      activeDot={{ r: 6 }}
                    />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trainings Tab Content */}
        <TabsContent value="trainings" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Trainings Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.trainings?.newTrainingsToday || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Trainings This Week</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.trainings?.newTrainingsThisWeek || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Trainings This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.trainings?.newTrainingsThisMonth || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Average Price</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(adminStats?.trainings?.averagePrice || 0)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Training Categories */}
          <Card>
            <CardHeader>
              <CardTitle>Training Categories</CardTitle>
              <CardDescription>Distribution of trainings by category</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {adminStats?.trainingCategories?.map((category: { category: string; count: number }) => (
                    <div key={category.category} className="flex items-center justify-between">
                      <span className="capitalize">{category.category || 'Uncategorized'}</span>
                      <div className="flex items-center">
                        <span className="font-medium">{formatNumber(category.count)}</span>
                        <span className="text-muted-foreground ml-2">
                          ({((category.count / adminStats?.trainings?.total) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="h-[300px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={adminStats?.trainingCategories}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="category"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value: number) => [
                          formatNumber(value),
                          'Count'
                        ]}
                      />
                      <Bar
                        dataKey="count"
                        fill={COLORS.green[4]}
                        name="Count"
                        radius={[4, 4, 0, 0]}
                      >
                        {adminStats?.trainingCategories?.map((_: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={COLORS.green[index % 5 + 1]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enrollments Tab Content */}
        <TabsContent value="enrollments" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Enrollments Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.enrollments?.newEnrollmentsToday || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Enrollments This Week</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.enrollments?.newEnrollmentsThisWeek || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">New Enrollments This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(adminStats?.enrollments?.newEnrollmentsThisMonth || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(adminStats?.enrollments?.totalRevenue || 0)}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Revenue Trends</CardTitle>
              <CardDescription>Monthly revenue from enrollments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={revenueTrendsData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.amber[4]} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={COLORS.amber[4]} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" />
                    <YAxis
                      tickFormatter={(value) => `$${value / 1000}k`}
                    />
                    <CartesianGrid strokeDasharray="3 3" />
                    <RechartsTooltip
                      formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke={COLORS.amber[5]}
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;